<script setup lang="ts">
import KsoOrderCreatedView from "@innoclouds/ui/templates/kso/ksoOrderCreated/KsoOrderCreated.vue";
import { useTranslation } from "i18next-vue";
import { computed } from "vue";
import { useDecorationStore } from "#/store/decoration";
import AnimNum from "#/ui/recountNumber/RecountNumber.vue";
import KsoBonusesPad from "../ksoBonusesPad/KsoBonusesPad.vue";

interface Props {
	immersive?: boolean;
	discount: number;
	bonuses: number;
	maxBonuses: number;
	total: number;
	deliveryCost?: number | null;
	currencySymbol?: string | null;
	bonusSign?: string;
	allowDecimalBonuses?: boolean;
	bonusesOpen?: boolean;
	decoration?: string;
}

const props = withDefaults(defineProps<Props>(), {
	immersive: true,
	deliveryCost: null,
	currencySymbol: null,
	bonusSign: "",
	allowDecimalBonuses: false,
	bonusesOpen: false,
	decoration: undefined,
});

const emit = defineEmits<{
	"apply-bonuses": [value: number];
	"close-bonuses": [];
}>();

const { t } = useTranslation();
const decor = useDecorationStore().screen(props.decoration);

const isSurcharge = computed(() => props.discount > 0);
const discountPercent = computed(() => {
	const base = props.total + props.bonuses - (props.deliveryCost ?? 0) - props.discount;
	return base > 0 ? Math.round((Math.abs(props.discount) / base) * 100) : 0;
});
const discountTitle = computed(() => {
	if (!isSurcharge.value && discountPercent.value > 0 && decor.visible("kso_discount_percent", false))
		return t(`kso.created.discount-percent`, { percent: discountPercent.value });
	return isSurcharge.value ? t(`cart.footer.supplement.title`) : t(`cart.footer.discount.title`);
});
</script>

<template>
	<KsoOrderCreatedView
		:immersive="props.immersive"
		:title="t(`kso.created.title`)"
		:show-discount="props.discount !== 0"
		:surcharge="isSurcharge"
		:discount-label="props.discount !== 0 ? discountTitle : ''"
		:show-delivery="props.deliveryCost !== null"
		:delivery-label="t(`cart.footer.delivery-cost.title`)"
		:show-bonuses="props.bonuses > 0"
		:bonuses-label="t(`cart.footer.bonus.title`)"
		:total-label="t(`cart.footer.final.title`)"
		:available-label="t(`kso.bonuses.available`)"
		:bonuses-open="props.bonusesOpen"
		:decor="decor"
	>
		<template #discount-value>
			<AnimNum :price="Math.abs(props.discount)" :symbol="props.currencySymbol" />
		</template>
		<template #delivery-value>
			<AnimNum :price="props.deliveryCost ?? 0" :symbol="props.currencySymbol" />
		</template>
		<template #bonuses-value>
			<AnimNum :price="props.bonuses" :symbol="props.currencySymbol" />
		</template>
		<template #total-value>
			<AnimNum :price="props.total" :symbol="props.currencySymbol" />
		</template>
		<template #available-value>
			<AnimNum :price="props.maxBonuses" />
		</template>
		<template #bonuses-pad>
			<KsoBonusesPad
				:max-withdraw="props.maxBonuses"
				:applied="props.bonuses"
				:allow-decimal="props.allowDecimalBonuses"
				:bonus-sign="props.bonusSign"
				:decoration="props.decoration"
				@apply="(value) => emit('apply-bonuses', value)"
				@close="emit('close-bonuses')"
			/>
		</template>
	</KsoOrderCreatedView>
</template>
